import { useRouter } from "@core/Api/useRouter";
import ApiUrlCreatorService from "@core-ui/ContextServices/ApiUrlCreator";
import { useDeferApi } from "@core-ui/hooks/useApi";
import { useArticlePropsStore } from "@core-ui/stores/ArticlePropsStore/ArticlePropsStore.provider";
import { useCatalogPropsStore } from "@core-ui/stores/CatalogPropsStore/CatalogPropsStore.provider";
import { useDiffMode } from "@ext/git/core/Diff/logic/hooks/useDiffMode";
import { disableLiveDiff, enableLiveDiff, useLiveDiffAttached } from "@ext/git/core/Diff/logic/live/liveDiffStore";
import { GitTreeScopeParser } from "@ext/versioning/GitTreeScopeParser";
import { useCallback, useRef } from "react";

export const useGotoRevision = () => {
	const router = useRouter();
	const attached = useLiveDiffAttached();
	const { isMarkdown } = useDiffMode();
	const catalogName = useCatalogPropsStore((state) => state.data?.name);
	const articlePath = useArticlePropsStore((state) => state.data?.ref?.path);
	const apiUrlCreator = ApiUrlCreatorService.value;
	const live = attached && !!catalogName && !!articlePath;
	const requested = useRef<{ commitOid: string; oldCommitOid?: string } | null>(null);

	const { call: gotoRevision } = useDeferApi<string, string>({
		onDone: (pathname) => {
			const hasCommitOid = pathname.includes("commit-") || pathname.includes("dif-");
			if (!hasCommitOid) {
				if (live) disableLiveDiff(router);
				else void router.pushPath(pathname);
				return;
			}
			const { commitOid, oldCommitOid } = requested.current ?? {};
			if (live && commitOid) {
				void enableLiveDiff({
					catalogName,
					articlePath,
					scope: GitTreeScopeParser.toString({ commit: commitOid }),
					oldScope: oldCommitOid ? GitTreeScopeParser.toString({ commit: oldCommitOid }) : undefined,
					view: isMarkdown ? "markdown" : "wysiwyg",
					apiUrlCreator,
					router,
				});
				return;
			}
			void router.pushPath(pathname, { diff: "1" });
		},
	});

	return useCallback(
		(commitOid: string, oldCommitOid?: string) => {
			requested.current = { commitOid, oldCommitOid };
			return gotoRevision({ url: (api) => api.getPathnameToRevision(commitOid, oldCommitOid) });
		},
		[gotoRevision],
	);
};
